import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, LoginInput, RegisterInput } from '../models/User';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (credentials: LoginInput) => Promise<void>;
    register: (userData: RegisterInput) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const token = authService.getAccessToken();
            if (!token) {
                setLoading(false);
                return;
            }

            // Since there's no /me endpoint, we'll rely on the token
            // You might want to add a user verification endpoint later
            setLoading(false);
        } catch (error) {
            console.error('Auth check failed:', error);
            authService.clearTokens();
            setUser(null);
            setLoading(false);
        }
    };

    const login = async (credentials: LoginInput) => {
        try {
            setError(null);
            const response = await authService.login(credentials);
            setUser(response.User);
        } catch (error: any) {
            setError(error.message);
            throw error;
        }
    };

    const register = async (userData: RegisterInput) => {
        try {
            setError(null);
            const response = await authService.register(userData);
            setUser(response.User);
        } catch (error: any) {
            setError(error.message);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
            setError(null);
        } catch (error: any) {
            setError(error.message);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, error, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
} 